import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getFormValues } from 'redux-form';

import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { isStepConnected } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import {
  isInitialStep,
  uiStepAllComponentConfigs,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import {
  getAllWorkflowActionSteps,
  getAllWorkflowConditionalSteps,
  isUpdateInquiryNextStepActionStep,
} from 'dashboard/apps/InquiryTemplateEditor/utils/workflow-steps';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
} from 'dashboard/models/inquiry-template-version';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { RootState } from 'dashboard/redux/store';
import { isUiStep } from 'lib/interfaces/StepsConfig';
import { isImageHostedComponentConfig } from 'lib/interfaces/ui-components';

export default () => {
  const editorFormValues = useSelector<RootState, any>(
    (state) => getFormValues(INQUIRY_TEMPLATE_EDITOR_FORM_NAME)(state) ?? {},
  );

  const steps = useMemo(() => {
    return editorFormValues.steps || [];
  }, [editorFormValues]);

  const workflowVersionMap = useMemo(() => {
    return editorFormValues.workflowVersions || {};
  }, [editorFormValues]);

  // We assume that `workflowVersionMap` is always up-to-date following any editing and any
  // steps referenced within are connected (i.e. no "orphaned" workflow versions).
  // We assume that `steps` does not necessarily contain only connected steps, and we must
  // prune this array for any disconnected steps prior to counting total steps.
  // We iteratively prune steps that are disconnected or are initial steps. However, one pass
  // is not sufficient, particularly in the case where `workflowVersionMap` or `steps` has a removal,
  // because the first pass will only remove steps directly referenced by that WFV. Once we
  // remove those direct-referenced steps, we need to remove steps referenced by those
  // direct-referenced steps and then on, until there are no more step removals.
  const numSteps = useMemo(() => {
    let currentSteps: InquiryTemplateVersionStep[] = steps;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const connectedSteps = currentSteps.filter(
        (step) =>
          isInitialStep(step) ||
          isStepConnected({
            stepName: step.name,
            steps: currentSteps,
            workflowVersionMap,
          }),
      );
      if (connectedSteps.length === currentSteps.length) {
        return connectedSteps.length;
      }
      currentSteps = connectedSteps;
    }
  }, [steps, workflowVersionMap]);

  const numConditionalSteps = useMemo(() => {
    return getAllWorkflowConditionalSteps(
      Object.values(workflowVersionMap).flatMap(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (workflowVersion: WorkflowVersion) => workflowVersion.attributes['steps-config'],
      ),
    ).length;
  }, [workflowVersionMap]);

  const numActions = useMemo(() => {
    return getAllWorkflowActionSteps(
      Object.values(workflowVersionMap)
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .map((workflowVersion: WorkflowVersion) => workflowVersion.attributes['steps-config'])
        .flat(),
    ).filter((workflowStep) => !isUpdateInquiryNextStepActionStep(workflowStep)).length;
  }, [workflowVersionMap]);

  const numNonInitialStepImageHostedAssets = useMemo(() => {
    return steps
      .filter(isUiStep)
      .filter(
        (step: InquiryTemplateVersionStepUi) =>
          !isInitialStep(step) &&
          isStepConnected({
            stepName: step.name,
            steps: steps,
            workflowVersionMap,
          }),
      )
      .flatMap((step: InquiryTemplateVersionStepUi) => {
        return uiStepAllComponentConfigs(step).filter(isImageHostedComponentConfig);
      }).length;
  }, [steps, workflowVersionMap]);

  return { numSteps, numActions, numNonInitialStepImageHostedAssets, numConditionalSteps };
};
